/**
 * Matrix report for a shop stock count.
 *
 * One builder feeds both the printed slip and the emailed PDF so the two
 * copies cannot drift apart. Nothing here writes stock — the report is a
 * record of what the shop counted.
 */

export interface ShopStockCountReportLine {
	id?: string
	product_id: string | null
	sku: string | null
	product_name: string | null
	reference_code: string | null
	color_id?: string | null
	color_name: string | null
	size_id?: string | null
	size_name: string | null
	image_url?: string | null
	counted_qty: number
	scan_source: 'manual' | 'barcode' | 'epc'
	qty_before_edit?: number | null
	edited_by?: string | null
	edited_by_name?: string | null
	edited_at?: string | null
}

export interface ShopStockCountReportInput {
	countNumber: string
	mode: 'one_by_one' | 'barcode' | 'uhf'
	locationName?: string | null
	startedByName?: string | null
	submittedByName?: string | null
	submittedAt: string
	lines: ShopStockCountReportLine[]
	company?: {
		company_name?: string | null
		logo_url?: string | null
		company_address?: string | null
		company_phone?: string | null
		company_email?: string | null
	} | null
}

export interface ShopStockCountMatrixColor {
	colorId: string
	colorName: string
	sizeQuantities: Record<string, number>
	sizeLineIds: Record<string, string>
	totalQty: number
}

export interface ShopStockCountMatrixGroup {
	productName: string
	referenceCode: string
	imageUrl: string
	colors: ShopStockCountMatrixColor[]
	totalQty: number
}

export interface ShopStockCountMatrix {
	groups: ShopStockCountMatrixGroup[]
	sizeColumns: string[]
	totalUnits: number
	totalLines: number
	totalSkus: number
}

export function escapeHtml(value: unknown): string {
	const str = value == null ? '' : String(value)
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

const SIZE_ORDER_HINT: Record<string, number> = {
	XXS: 10,
	XS: 20,
	S: 30,
	M: 40,
	L: 50,
	XL: 60,
	XXL: 70,
	'2XL': 70,
	XXXL: 80,
	'3XL': 80,
	'4XL': 90,
	'5XL': 100,
	'NO SIZE': 9999,
}

function sizeSortKey(name: string): number {
	const key = name.trim().toUpperCase()
	if (SIZE_ORDER_HINT[key] != null) return SIZE_ORDER_HINT[key]
	const numeric = Number(key)
	if (!Number.isNaN(numeric)) return 200 + numeric
	return 500
}

/**
 * Groups counted lines into product → color → size cells for the matrix view.
 * Pure: safe to reuse for the on-screen report, print HTML, and PDF attachment.
 */
export function buildShopStockCountMatrix(lines: ShopStockCountReportLine[]): ShopStockCountMatrix {
	const groups = new Map<string, ShopStockCountMatrixGroup>()
	const sizeSet = new Set<string>()
	let totalUnits = 0

	for (const line of lines) {
		const qty = Math.max(0, Number(line.counted_qty) || 0)
		if (qty <= 0) continue

		totalUnits += qty
		const productName = (line.product_name || line.sku || 'Unnamed product').trim()
		const colorId = line.color_id || line.color_name || 'no-color'
		const colorName = (line.color_name || '-').trim() || '-'
		const sizeName = (line.size_name || 'No Size').trim() || 'No Size'
		sizeSet.add(sizeName)

		let group = groups.get(productName)
		if (!group) {
			group = {
				productName,
				referenceCode: line.reference_code || '',
				imageUrl: line.image_url || '',
				colors: [],
				totalQty: 0,
			}
			groups.set(productName, group)
		}

		if (!group.referenceCode && line.reference_code) group.referenceCode = line.reference_code
		if (!group.imageUrl && line.image_url) group.imageUrl = line.image_url

		let color = group.colors.find((c) => c.colorId === colorId)
		if (!color) {
			color = {
				colorId,
				colorName,
				sizeQuantities: {},
				sizeLineIds: {},
				totalQty: 0,
			}
			group.colors.push(color)
		}

		color.sizeQuantities[sizeName] = (color.sizeQuantities[sizeName] || 0) + qty
		if (line.id && !color.sizeLineIds[sizeName]) color.sizeLineIds[sizeName] = line.id
		color.totalQty += qty
		group.totalQty += qty
	}

	const sizeColumns = Array.from(sizeSet).sort((a, b) => {
		const diff = sizeSortKey(a) - sizeSortKey(b)
		return diff !== 0 ? diff : a.localeCompare(b)
	})

	const sortedGroups = Array.from(groups.values())
		.map((group) => ({
			...group,
			colors: [...group.colors].sort((a, b) => a.colorName.localeCompare(b.colorName)),
		}))
		.sort((a, b) => a.productName.localeCompare(b.productName))

	return {
		groups: sortedGroups,
		sizeColumns,
		totalUnits,
		totalLines: lines.filter((l) => (l.counted_qty || 0) > 0).length,
		totalSkus: sortedGroups.length,
	}
}

const MODE_LABEL: Record<ShopStockCountReportInput['mode'], string> = {
	one_by_one: 'One by one',
	barcode: 'Barcode scanner',
	uhf: 'UHF scanner',
}

function matrixTableHtml(matrix: ShopStockCountMatrix): string {
	if (matrix.groups.length === 0) {
		return '<p class="empty">No items were counted.</p>'
	}

	const sizeHeaders = matrix.sizeColumns
		.map((size) => `<th class="num">${escapeHtml(size)}</th>`)
		.join('')

	const body = matrix.groups
		.map((group) => {
			const rows = group.colors
				.map((color, colorIndex) => {
					const sizeCells = matrix.sizeColumns
						.map((size) => {
							const qty = color.sizeQuantities[size]
							return `<td class="num ${qty ? 'strong' : 'muted'}">${qty ? escapeHtml(qty) : '·'}</td>`
						})
						.join('')

					const productCell =
						colorIndex === 0
							? `<td class="product" rowspan="${group.colors.length}">
									<div class="product-name">${escapeHtml(group.productName)}</div>
									${group.referenceCode ? `<div class="ref">${escapeHtml(group.referenceCode)}</div>` : ''}
									<div class="sub">${escapeHtml(group.totalQty)} unit${group.totalQty === 1 ? '' : 's'}</div>
								</td>`
							: ''

					return `
						<tr>
							${productCell}
							<td class="color">${escapeHtml(color.colorName)}</td>
							${sizeCells}
							<td class="num strong">${escapeHtml(color.totalQty)}</td>
						</tr>`
				})
				.join('')

			return rows
		})
		.join('')

	return `
		<table class="matrix">
			<thead>
				<tr>
					<th>Product</th>
					<th>Color</th>
					${sizeHeaders}
					<th class="num">Total</th>
				</tr>
			</thead>
			<tbody>${body}</tbody>
		</table>`
}

/** Builds the branded report document used for print and the emailed PDF. */
export function buildShopStockCountReportHtml(input: ShopStockCountReportInput): string {
	const matrix = buildShopStockCountMatrix(input.lines)
	const company = input.company
	const companyName = company?.company_name || ''
	const submitted = new Date(input.submittedAt)

	const logo = company?.logo_url
		? `<img class="logo" src="${escapeHtml(company.logo_url)}" alt="${escapeHtml(companyName)}" crossorigin="anonymous" />`
		: ''

	const contact = [company?.company_address, company?.company_phone, company?.company_email]
		.filter(Boolean)
		.map((part) => escapeHtml(part))
		.join(' &middot; ')

	const meta = [
		['Count', input.countNumber],
		['Location', input.locationName || 'Shop'],
		['Counted by', input.submittedByName || input.startedByName || '-'],
		['Method', MODE_LABEL[input.mode] || input.mode],
		['Finished at', submitted.toLocaleString()],
	]
		.map(
			([label, value]) =>
				`<div class="kv"><span class="kv-label">${escapeHtml(label)}</span><span class="kv-value">${escapeHtml(value)}</span></div>`
		)
		.join('')

	return `<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8" />
	<title>Shop Stock Count - ${escapeHtml(input.countNumber)}</title>
	<style>
		* { box-sizing: border-box; }
		body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 36px; color: #1f2937; font-size: 13px; margin: 0; background: #fff; }
		.header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; border-bottom: 3px solid #0f766e; padding-bottom: 16px; margin-bottom: 20px; }
		.logo { max-height: 56px; max-width: 180px; object-fit: contain; }
		.title { font-size: 24px; font-weight: 700; color: #0f766e; margin: 0; }
		.subtitle { color: #6b7280; margin-top: 4px; }
		.company { text-align: right; }
		.company-name { font-weight: 700; font-size: 15px; }
		.company-contact { color: #6b7280; font-size: 11px; margin-top: 4px; max-width: 260px; }
		.meta { display: flex; flex-wrap: wrap; gap: 8px 28px; margin-bottom: 18px; }
		.kv { display: flex; flex-direction: column; }
		.kv-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #9ca3af; font-weight: 600; }
		.kv-value { font-weight: 600; color: #111827; }
		.totals { display: flex; gap: 12px; margin-bottom: 22px; }
		.tile { flex: 1; border-radius: 10px; padding: 12px 14px; border: 1px solid; background: #f0fdfa; border-color: #99f6e4; }
		.tile-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; color: #0f766e; }
		.tile-value { font-size: 26px; font-weight: 800; line-height: 1.1; margin-top: 2px; color: #0f766e; }
		.tile-sub { font-size: 10px; color: #6b7280; }
		.note { border-radius: 8px; padding: 10px 14px; font-weight: 600; margin-bottom: 22px; font-size: 12px; background: #ecfeff; color: #155e75; border: 1px solid #a5f3fc; }
		table.matrix { width: 100%; border-collapse: collapse; }
		table.matrix th { text-align: left; padding: 7px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; background: #f0fdfa; color: #0f766e; border-bottom: 2px solid #99f6e4; }
		table.matrix td { padding: 7px 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
		table.matrix td.num, table.matrix th.num { text-align: center; }
		table.matrix td.strong { font-weight: 700; }
		table.matrix td.muted { color: #d1d5db; }
		table.matrix .product-name { font-weight: 700; color: #111827; }
		table.matrix .ref { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11px; color: #6b7280; }
		table.matrix .sub { font-size: 11px; color: #6b7280; margin-top: 2px; }
		.empty { color: #9ca3af; font-style: italic; text-align: center; padding: 24px; }
		.footer { margin-top: 26px; padding-top: 12px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 10px; display: flex; justify-content: space-between; }
		@media print {
			body { padding: 14px; }
			* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
			@page { size: A4; margin: 12mm; }
		}
	</style>
</head>
<body>
	<div class="header">
		<div>
			<h1 class="title">Shop Stock Count</h1>
			<div class="subtitle">Physical count at the shop — audit record only</div>
		</div>
		<div class="company">
			${logo}
			${companyName ? `<div class="company-name">${escapeHtml(companyName)}</div>` : ''}
			${contact ? `<div class="company-contact">${contact}</div>` : ''}
		</div>
	</div>

	<div class="meta">${meta}</div>

	<div class="totals">
		<div class="tile">
			<div class="tile-label">Units counted</div>
			<div class="tile-value">${escapeHtml(matrix.totalUnits)}</div>
			<div class="tile-sub">${escapeHtml(matrix.totalLines)} line${matrix.totalLines === 1 ? '' : 's'}</div>
		</div>
		<div class="tile">
			<div class="tile-label">Products</div>
			<div class="tile-value">${escapeHtml(matrix.totalSkus)}</div>
			<div class="tile-sub">${escapeHtml(matrix.sizeColumns.length)} size column${matrix.sizeColumns.length === 1 ? '' : 's'}</div>
		</div>
	</div>

	<div class="note">This report does not change inventory. It records what was counted at the shop.</div>

	${matrixTableHtml(matrix)}

	<div class="footer">
		<span>Generated by Shop Stock Count. Audit record only — stock is unchanged.</span>
		<span>${escapeHtml(new Date().toLocaleString())}</span>
	</div>
</body>
</html>`
}

export function printShopStockCountReport(html: string): void {
	const popup = window.open('', '_blank', 'width=980,height=760')

	if (popup) {
		popup.document.write(
			html.replace('</body>', '<script>window.onload = function () { window.print(); };</script></body>')
		)
		popup.document.close()
		return
	}

	const frame = document.createElement('iframe')
	frame.setAttribute('aria-hidden', 'true')
	frame.style.position = 'fixed'
	frame.style.right = '0'
	frame.style.bottom = '0'
	frame.style.width = '0'
	frame.style.height = '0'
	frame.style.border = '0'
	document.body.appendChild(frame)

	const doc = frame.contentDocument
	if (!doc) {
		document.body.removeChild(frame)
		return
	}

	doc.open()
	doc.write(html)
	doc.close()

	const run = () => {
		try {
			frame.contentWindow?.focus()
			frame.contentWindow?.print()
		} finally {
			window.setTimeout(() => {
				if (frame.parentNode) frame.parentNode.removeChild(frame)
			}, 60_000)
		}
	}

	window.setTimeout(run, 350)
}
